import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { DateInputPropsType } from '../../utils/types'
import DateInput, { DateTimeInputComponent } from './DateInput'
import moment from 'moment'
import { DATE_FIELD_TYPES, DATE_INPUT_CONSTANTS, NAMESPACE } from '../../utils'

const DateInputWrapper = ({
  label,
  name,
  className,
  showClear,
  isDisabled,
  handleChange,
  placeholder,
  showIcon,
  tabIndex,
  value,
  wrapperClassName,
  defaultValue,
  error,
  required,
  max,
  min,
  type = DATE_FIELD_TYPES.DATE_TYPE,
  hasPermission = true,
  dataQaId,
}: DateInputPropsType & {
  name: string
  error?: string
  hasPermission?: boolean
  type?: DATE_FIELD_TYPES
}) => {
  const hasValue = !!value || !!defaultValue || !!placeholder

  return (
    <div data-qa-id={dataQaId ?? name} className="w-full font-primary">
      <div
        className={`group relative w-full custom-input-main-container font-primary date-input-custom-wrapper`}
      >
        {/* Floating Label */}
        {label && (
          <label
            className={`absolute ${
              isDisabled ? 'text-accent-300' : 'text - black'
            } left-3 z-10 transition-all duration-200 ease-in-out 
      pointer-events-none truncate max-w-[calc(100%-1.75rem)]
      font-primary font-extralight  ${
        isDisabled
          ? 'text-accent-300'
          : 'text-black group-hover:text-secondary-500'
      }
                  ${
                    hasValue
                      ? `-top-[.5rem] text-xs bg-white px-1 pb-[0] group-focus-within:text-secondary-500`
                      : `group-focus-within:-top-[.5rem] group-focus-within:text-xs group-focus-within:bg-white group-focus-within:px-1 group-focus-within:pb-[0] top-[.9rem] text-xs`
                  }
                `}
          >
            {label}
            {required && (
              <span
                className={`ml-1 ${
                  isDisabled ? 'text-accent-300' : 'text-error-500'
                }`}
              >
                *
              </span>
            )}
          </label>
        )}

        {/* Input container with border and background */}
        <div
          className={`custom-input-container w-full inline-flex items-center font-light px-3 py-1.5 rounded border 
                ${
                  isDisabled
                    ? 'border-border-200 bg-accent-50 cursor-not-allowed text-accent-400'
                    : 'border-border-150 bg-white group-focus-within:border-secondary-500 group-hover:border-secondary-500 hover:border-secondary-500'
                } ${className}`}
        >
          {!hasPermission ? (
            <input
              value={NAMESPACE.REDACTED}
              disabled
              className="w-full bg-transparent outline-none border-none text-xs p-[0]"
            />
          ) : null}
          {hasPermission &&
            (type === DATE_FIELD_TYPES.DATE_TYPE ? (
              <DateInput
                name={name}
                isDisabled={isDisabled}
                value={
                  value
                    ? moment(value).format(
                        DATE_INPUT_CONSTANTS.COMMON_DATE_FORMAT
                      )
                    : null
                }
                defaultValue={defaultValue}
                handleChange={(dateStr) => handleChange?.(dateStr)}
                className="w-full bg-transparent outline-none border-none text-xs p-[0]"
                tabIndex={tabIndex}
                showClear={showClear}
                showIcon={showIcon}
                max={max}
                min={min}
                wrapperClassName={wrapperClassName}
                placeholder={placeholder}
              />
            ) : (
              <DateTimeInputComponent
                name={name}
                isDisabled={isDisabled}
                value={value}
                defaultValue={defaultValue}
                handleChange={(dateStr) => handleChange?.(dateStr)}
                className="w-full bg-transparent outline-none border-none text-xs p-[0]"
                tabIndex={tabIndex}
                showClear={showClear}
                showIcon={showIcon}
                max={max}
                min={min}
                wrapperClassName={wrapperClassName}
                placeholder={placeholder}
              />
            ))}
        </div>
        {error && (
          <span className="text-error-500 text-xxs mt-1 block font-primary">
            {error}
          </span>
        )}
      </div>
    </div>
  )
}

const FormDateInput = <U extends FieldValues>({
  label,
  name,
  className,
  showClear,
  isDisabled,
  control,
  handleChange,
  showIcon,
  tabIndex,
  defaultValue,
  max,
  min,
  placeholder,
  required,
  hasPermission,
  type,
  dataQaId,
}: DateInputPropsType & {
  name: Path<U>
  control: Control<U>
  min: string
  max: string
  hasPermission?: boolean
  type?: DATE_FIELD_TYPES
}) => {
  const getRules = (required: boolean | undefined) => {
    if (typeof required === 'string') {
      return required
    } else if (required) {
      return NAMESPACE?.REQUIRED_FIELD
    }
    return false
  }
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: getRules(required),
      }}
      render={({ field, fieldState }) => (
        <div className={`relative w-[100%] focus:outline-none ${className}`}>
          <DateInputWrapper
            name={field?.name}
            isDisabled={isDisabled}
            label={label}
            value={field.value ?? defaultValue}
            required={required}
            className={className}
            handleChange={(date) => {
              if (isDisabled) return
              field.onChange(date)
              handleChange && handleChange(date)
            }}
            showIcon={showIcon}
            showClear={showClear}
            tabIndex={tabIndex}
            error={fieldState?.error?.message}
            max={max}
            min={min}
            placeholder={placeholder}
            hasPermission={hasPermission}
            type={type}
            dataQaId={dataQaId}
          />
        </div>
      )}
    />
  )
}

export default FormDateInput
